import React, { lazy } from 'react'
import { Routes, Route, Navigate } from 'react-router'
import Layout from './components/layouts/Layout'
import LazyLoader from './rsmCoreComponents/LazyLoader'
import ProtectedRoute from './components/ProtectedRoute'
import ProtectedRouteError from './components/ProtectedRouteError'
import Home from './pages/Home'
import Engagement from './pages/Engagement'
import EngagementList from './pages/EngagementList'
import Docs from './pages/Docs'
import Insight from './pages/Insight'
import Insights from './pages/Insights'
import Notifications from './pages/Notifications'
import Profile from './pages/Profile'
import ProjectsPage from './pages/ProjectsPage'
import ReportAnIssue from './pages/ReportAnIssue'
import Faq from './pages/Faq'
import FaqRedesign from './pages/Faq/FaqRedesign'
import FeaturesPage from './pages/FeaturesPage'
import Support from './pages/Support'
import SupportPage from './pages/Support/SupportPage'
import FeedbackForm from './pages/Feedback'
import NoMatch from './pages/NoMatch'
import ContactUsBaseLayout from './pages/Support/ContactUsBaseLayout'
import PaymentHistoryLayout from './components/Invoicing/Invoices/PaymentHistoryLayout'
import InvoiceProtectedLayout from './pages/Invoicing'
import InvoicePaymentSuccess from './components/Invoicing/Invoices/InvoicePaymentSuccess'
import InvoicesPaymentTable from './components/Invoicing/Invoices/InvoicesPaymentTable'
import QuestionnaireBaseLayout from './pages/Questionnaire/QuestionnaireBaseLayout'
import {
  MyQuestionnaireLayout,
  SearchAndAssignLayout,
} from './components/Questionnaire'
import {
  AccountManagement,
  DashboardLayout,
  InvoicesLayout,
  PaymentType,
} from './components/Invoicing'
import {
  CEM_FEATURE_PAYMENT_ACCOUNT_MANAGEMENT,
  CEM_FEATURE_PAYMENT_CLIENT_ADMIN,
  CEM_FEATURE_PAYMENT_PAY_INVOICES,
  CEM_FEATURE_PAYMENT_VIEW_INVOICES_AND_DOCUMENTS,
  CEM_FEATURE_QUESTIONNAIRE_ASSIGN,
  CEM_FEATURE_QUESTIONNAIRE_EXTERNAL_COMPLETE,
  INVOICING_FEATURE_AUTOPAY,
} from './utils/constants/constants'
import { isFeatureFlagEnabled } from './rsmCoreComponents/utils/featureFlagUtils'
import { useAppSelector } from './utils/hooks'
import { getCemFeatures, getUserInfo } from './store/userInfo/userInfoSlice'
import Report from './pages/PowerBI/Report'

const DocumentRequestsPage = LazyLoader(
  lazy(() => import('./pages/DocumentRequestsPage')),
)

export const AppRoutes = () => {
  const cemFeatures = useAppSelector(getCemFeatures)
  const userInfo = useAppSelector(getUserInfo)
  const isSupportEnabled = isFeatureFlagEnabled(
    'Support',
    userInfo.emailAddress,
    cemFeatures,
  )
  const isUserProfileEnabled = isFeatureFlagEnabled(
    'UserProfile',
    userInfo.emailAddress,
    cemFeatures,
  )
  const isFAQRedesignEnabled = isFeatureFlagEnabled(
    'FAQRedesign',
    userInfo.emailAddress,
    cemFeatures,
  )
  const isQuestionnairesEnabled = isFeatureFlagEnabled(
    'Questionnaires',
    userInfo.emailAddress,
    cemFeatures,
  )

  const supportProtectedRoute = (
    children: React.ReactNode,
  ): React.ReactNode => (
    <ProtectedRoute
      feature="Support"
      featureFallback={<Navigate to="/support" replace />}
      matchType="all">
      {children}
    </ProtectedRoute>
  )

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route index element={<Home />} />
        <Route path="/" element={<Home />} />
        <Route
          path="/engagements"
          element={
            <ProtectedRoute feature="Targeting_Engagement" matchType="any">
              <EngagementList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/engagements/:engagementId"
          element={
            <ProtectedRoute feature="Targeting_Engagement" matchType="any">
              <Engagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/engagements/:engagementId/requests/*"
          element={
            <ProtectedRoute
              feature={['DocumentRequest', 'Targeting_Engagement']}
              matchType="any">
              <DocumentRequestsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/engagements/:engagementId/docs"
          element={
            <ProtectedRoute
              feature={['DocumentRequest', 'Targeting_Engagement']}
              requireEmployee
              matchType="any">
              <Docs />
            </ProtectedRoute>
          }
        />
        <Route path="/insights" element={<Insights />} />
        <Route path="/insights/:insightId" element={<Insight />} />
        <Route
          path="/invoicing"
          element={
            <ProtectedRoute
              feature="Invoicing"
              cemFeature={[
                CEM_FEATURE_PAYMENT_PAY_INVOICES,
                CEM_FEATURE_PAYMENT_VIEW_INVOICES_AND_DOCUMENTS,
                CEM_FEATURE_PAYMENT_CLIENT_ADMIN,
              ]}
              matchType="any">
              <InvoiceProtectedLayout />
            </ProtectedRoute>
          }>
          <Route
            index
            element={
              <ProtectedRoute
                cemFeature={CEM_FEATURE_PAYMENT_CLIENT_ADMIN}
                cemFeatureFallback={
                  <Navigate to="/invoicing/invoices" replace />
                }
                matchType="any">
                <Navigate to="/invoicing/dashboard" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="dashboard"
            element={
              <ProtectedRoute
                cemFeature={CEM_FEATURE_PAYMENT_CLIENT_ADMIN}
                matchType="any">
                <DashboardLayout />
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoicing/dashboard/:clientId"
            element={
              <ProtectedRoute
                cemFeature={CEM_FEATURE_PAYMENT_CLIENT_ADMIN}
                matchType="any">
                <DashboardLayout />
              </ProtectedRoute>
            }
          />

          <Route
            path="/invoicing/invoices/:clientId"
            element={
              <ProtectedRoute
                cemFeature={[
                  CEM_FEATURE_PAYMENT_PAY_INVOICES,
                  CEM_FEATURE_PAYMENT_VIEW_INVOICES_AND_DOCUMENTS,
                  CEM_FEATURE_PAYMENT_CLIENT_ADMIN,
                ]}
                matchType="any">
                <InvoicesLayout />
              </ProtectedRoute>
            }
          />
          <Route
            path="invoices"
            element={
              <ProtectedRoute
                cemFeature={[
                  CEM_FEATURE_PAYMENT_VIEW_INVOICES_AND_DOCUMENTS,
                  CEM_FEATURE_PAYMENT_PAY_INVOICES,
                ]}
                matchType="any">
                <InvoicesLayout />
              </ProtectedRoute>
            }>
            <Route path="payment-type" element={<PaymentType />} />
            <Route
              path="pay-on-account"
              element={<PaymentType isPayOnAccount />}
            />
            {/* add protected route for autopay feature flag */}
            <Route
              path="autopay"
              element={
                <ProtectedRoute
                  feature={INVOICING_FEATURE_AUTOPAY}
                  matchType="all">
                  <PaymentType isAutopay />
                </ProtectedRoute>
              }
            />
            <Route path="payment-success" element={<InvoicePaymentSuccess />} />
            <Route path="pay-invoices" element={<InvoicesPaymentTable />} />
          </Route>
          <Route
            path="payments"
            element={
              <ProtectedRoute
                cemFeature={[
                  CEM_FEATURE_PAYMENT_VIEW_INVOICES_AND_DOCUMENTS,
                  CEM_FEATURE_PAYMENT_PAY_INVOICES,
                ]}
                matchType="any">
                <PaymentHistoryLayout />
              </ProtectedRoute>
            }
          />
          <Route
            path="account-management"
            element={
              <ProtectedRoute
                cemFeature={[
                  CEM_FEATURE_PAYMENT_VIEW_INVOICES_AND_DOCUMENTS,
                  CEM_FEATURE_PAYMENT_PAY_INVOICES,
                ]}
                matchType="any">
                <ProtectedRoute
                  cemFeature={CEM_FEATURE_PAYMENT_ACCOUNT_MANAGEMENT}
                  matchType="any">
                  <AccountManagement />
                </ProtectedRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoicing/account-management/:clientId"
            element={
              <ProtectedRoute
                cemFeature={[
                  CEM_FEATURE_PAYMENT_VIEW_INVOICES_AND_DOCUMENTS,
                  CEM_FEATURE_PAYMENT_PAY_INVOICES,
                ]}
                matchType="any">
                <ProtectedRoute
                  cemFeature={CEM_FEATURE_PAYMENT_ACCOUNT_MANAGEMENT}
                  matchType="any">
                  <AccountManagement />
                </ProtectedRoute>
              </ProtectedRoute>
            }
          />
        </Route>
        <Route path="/questionnaires" element={<QuestionnaireBaseLayout />}>
          <Route
            index
            element={
              isQuestionnairesEnabled ? (
                <ProtectedRoute
                  cemFeature={CEM_FEATURE_QUESTIONNAIRE_ASSIGN}
                  cemFeatureFallback={
                    <Navigate to="/questionnaires/my" replace />
                  }
                  matchType="any">
                  <Navigate to="/questionnaires/queue" replace />
                </ProtectedRoute>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/questionnaires/my"
            element={
              isQuestionnairesEnabled ? (
                <ProtectedRoute
                  feature="Questionnaires"
                  cemFeature={[CEM_FEATURE_QUESTIONNAIRE_EXTERNAL_COMPLETE]}
                  cemFeatureFallback={
                    <Navigate to="/questionnaires/queue" replace />
                  }
                  matchType="any">
                  <MyQuestionnaireLayout />
                </ProtectedRoute>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/questionnaires/queue"
            element={
              isQuestionnairesEnabled ? (
                <ProtectedRoute
                  feature="Questionnaires"
                  cemFeature={[CEM_FEATURE_QUESTIONNAIRE_ASSIGN]}
                  cemFeatureFallback={
                    <Navigate to="/questionnaires/my" replace />
                  }
                  matchType="any">
                  <SearchAndAssignLayout />
                </ProtectedRoute>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
        </Route>
        <Route path="/projects/*" element={<ProjectsPage />} />
        <Route path="/notifications" element={<Notifications />} />
        {isUserProfileEnabled && (
          <Route path="/profile" element={<Profile />} />
        )}

        <Route
          path="/reportanissue"
          element={
            isSupportEnabled ? <ProtectedRouteError /> : <ReportAnIssue />
          }
        />
        <Route path="/faq" element={<Faq />} />
        <Route path="/debug/features" element={<FeaturesPage />} />
        <Route path="*" element={<NoMatch />} />
        <Route
          path="/support"
          element={
            isSupportEnabled ? (
              <Navigate to="/contact-us/support-page" replace />
            ) : (
              <Support />
            )
          }
        />
        <Route
          path="/contact-us"
          element={supportProtectedRoute(<ContactUsBaseLayout />)}>
          <Route
            index
            element={supportProtectedRoute(
              <Navigate to="/contact-us/support-page" replace />,
            )}
          />
          <Route
            path="support-page"
            element={supportProtectedRoute(<SupportPage />)}
          />
          <Route
            path="faq"
            element={
              isFAQRedesignEnabled
                ? supportProtectedRoute(<FaqRedesign />)
                : supportProtectedRoute(<Faq />)
            }
          />
          <Route
            path="feedback"
            element={supportProtectedRoute(<FeedbackForm />)}
          />
        </Route>
        <Route path="/Report/:reportName" element={<Report />} />
      </Route>
    </Routes>
  )
}

export default AppRoutes
